import React from "react";
import { formatBillingAmount } from "./_shared";
import { useLanguage } from "@/lib/LanguageContext";
import { instantDay } from "../../lib/calendarDay";
import { formatMoneyCode } from "../../lib/formatMoney";
import { currencyLabel } from "../../lib/currencies";

// One badge per state, and the NON-active states needed one as much as the others:
// `suspended`, `cancelled` and `past_due` all fell into the same pink fallback, with no way
// to tell "you are about to be cut off" from "you are cut off" or "it is over". The colour
// carries the urgency, the (i18n) label carries the meaning: the raw status value used to
// show in technical English whatever the language.
const STATUS_BADGE = {
  active: "bg-emerald-500/20 text-emerald-300",
  pending: "bg-amber-500/20 text-amber-300",
  past_due: "bg-amber-500/25 text-amber-200",
  suspended: "bg-rose-500/25 text-rose-200",
  cancelled: "bg-sol-ivory/15 text-sol-ivory/70",
};

/**
 * @param {object|null} customPrice  `billing/summary.custom_price` —
 *   `{price: {cents, currency}, cycle: "monthly"|"annual", ...}` while a negotiated price is
 *   in force. It replaces the plan's list price, per ITS cycle (a practice bills one cycle).
 */
export default function CurrentPlanCard({ tenant, plan, trial, customPrice = null, t }) {
  const { lang } = useLanguage();
  const listPrice = formatBillingAmount(plan.prices?.[tenant.currency], tenant.currency);
  const negotiated = customPrice ? formatMoneyCode(customPrice.price.cents, customPrice.price.currency) : null;
  const meta = [
    [t("bill.activatedOn"), instantDay(tenant.activated_at, lang) || "—"],
    [t("bill.currency"), tenant.currency ? currencyLabel(tenant.currency) : "—"],
    [t("bill.stripeCustomer"), tenant.stripe_customer_id || "—"],
  ];
  return (
    <div
      className="bg-gradient-to-br from-sol-midnight to-sol-slate text-sol-ivory rounded-3xl p-8"
      data-testid="current-plan-card"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-sol-gold mb-2">{t("bill.currentPlan")}</div>
          <div className="font-sol font-semibold text-3xl">{plan.name || tenant.plan_id}</div>
          <div className="text-sol-ivory/60 text-sm mt-1">{plan.tagline}</div>
        </div>
        <div className="text-right">
          <div className="font-sol font-semibold text-3xl">
            {negotiated || (plan.is_custom ? t("bill.from", { price: listPrice }) : listPrice)}
          </div>
          <div className="text-sol-ivory/60 text-xs mt-1">
            {t(customPrice?.cycle === "annual" ? "bill.cycle.perYear" : "bill.cycle.perMonth")}
          </div>
          <span
            data-testid="plan-status-badge"
            className={`inline-block mt-2 px-2 py-0.5 rounded text-[10px] uppercase ${STATUS_BADGE[tenant.status] || "bg-rose-500/20 text-rose-300"}`}
          >
            {t(`bill.status.${tenant.status}`, {}, tenant.status)}
          </span>
          {/* Additive to STATUS_BADGE (keyed by `tenant.status`, untouched): a trialing
              practice is `tenant.status == "active"` by design (§1 of the wire contract —
              the trial is a BILLING fact, never a service state), so the badge above would
              otherwise read "Active" with no sign that Stripe has not charged yet. */}
          {customPrice && (
            <span
              data-testid="plan-custom-price-chip"
              className="inline-block mt-2 ml-2 px-2 py-0.5 rounded text-[10px] uppercase bg-sol-gold/20 text-sol-gold"
            >
              {t("bill.customPrice.chip")}
            </span>
          )}
          {trial?.state === "trialing" && (
            <span
              data-testid="plan-trial-chip"
              className="inline-block mt-2 ml-2 px-2 py-0.5 rounded text-[10px] uppercase bg-sky-500/20 text-sky-300"
            >
              {t("bill.trial.chip")}
            </span>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-8 pt-6 border-t border-sol-ivory/10 text-sm">
        {meta.map(([k, v]) => (
          <div key={k}>
            <div className="text-sol-ivory/50 text-xs">{k}</div>
            <div className="text-sol-ivory mt-1 font-mono text-xs truncate">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
